import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useRouter } from 'next/router'

import type {
  AvailableFeaturesQuery,
  BalanceQuery,
  MonitoredBankAccountsQuery,
  MonitoredBankAccountTransactionsQuery,
  MonitoredBankAccountTransactionsQueryVariables,
  PaymentAccountsQuery,
  SubscriptionFragmentFragment,
  UserQuery,
} from '@graphql/react-apollo'
import {
  BankAccountTransactionSort,
  useAvailableFeaturesQuery,
  useBalanceQuery,
  useMonitoredBankAccountsQuery,
  useMonitoredBankAccountTransactionsQuery,
  usePaymentAccountsQuery,
  useSubscriptionsQuery,
  useUserQuery,
} from '@graphql/react-apollo'

import type { ClientSessionData } from '@web-app/hoc/withSession'
import {
  demoMonitoredAccount,
  generateDemoTransactions,
} from '@web-app/lib/demoData'
import {
  DEFAULT_POST_LOGIN_DESTINATION,
  POST_REGISTRATION_DESTINATION,
  VERIFY_PHONE_PATH,
} from '@web-app/utils/auth/constants'
import { withRedirect } from '@web-app/utils/auth/loginRedirect'

// Future: scope this provider to the authenticated surfaces instead of a
// global mount that skips when signed out.
type SyncStatus = 'idle' | 'syncing' | 'error'

interface SyncState {
  syncStatus: SyncStatus
  lastSyncedAt: Date | null
}

interface UserDataContextValue {
  syncState: SyncState
  user: UserQuery['user'] | undefined
  monitoredBankAccounts:
    | MonitoredBankAccountsQuery['monitoredBankAccounts']
    | undefined
  monitoredBankAccountTransactions:
    | MonitoredBankAccountTransactionsQuery['monitoredBankAccountTransactions']
    | undefined
  availableFeatures: AvailableFeaturesQuery['availableFeatures'] | undefined
  balance: BalanceQuery['balance'] | undefined
  // Funding rails, linked and unlinked. App-wide: their health drives prompts.
  paymentAccounts: PaymentAccountsQuery['paymentAccounts'] | undefined
  // The in-effect subscription; null when none.
  membership: SubscriptionFragmentFragment | null
  isDemo: boolean
  demoOverride: boolean | null
  setDemoOverride: (value: boolean | null) => void
  isAuthenticated: boolean
  userLoading: boolean
  monitoredBankAccountsLoading: boolean
  monitoredBankAccountTransactionsLoading: boolean
  balanceLoading: boolean
  paymentAccountsLoading: boolean
  membershipLoading: boolean
  refetchUser: () => void
  refetchMonitoredBankAccounts: () => void
  refetchMonitoredBankAccountTransactions: (
    variables?: Partial<MonitoredBankAccountTransactionsQueryVariables>,
  ) => void
  refetchBalance: () => void
  refetchPaymentAccounts: () => void
  refetchMembership: () => void
}

const noop = () => undefined

const UserDataContext = createContext<UserDataContextValue>({
  syncState: {
    syncStatus: 'idle',
    lastSyncedAt: null,
  },
  user: undefined,
  monitoredBankAccounts: undefined,
  monitoredBankAccountTransactions: undefined,
  availableFeatures: undefined,
  balance: undefined,
  paymentAccounts: undefined,
  membership: null,
  isDemo: false,
  demoOverride: null,
  setDemoOverride: noop,
  isAuthenticated: false,
  userLoading: true,
  monitoredBankAccountsLoading: true,
  monitoredBankAccountTransactionsLoading: true,
  balanceLoading: true,
  paymentAccountsLoading: true,
  membershipLoading: true,
  refetchUser: noop,
  refetchMonitoredBankAccounts: noop,
  refetchMonitoredBankAccountTransactions: noop,
  refetchBalance: noop,
  refetchPaymentAccounts: noop,
  refetchMembership: noop,
})

export function UserDataProvider({
  sessionData,
  children,
}: {
  sessionData?: ClientSessionData
  children: React.ReactNode
}) {
  const isAuthenticated = Boolean(sessionData?.user?.id)

  const {
    data: userData,
    loading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useUserQuery({
    variables: { categoriesFirst: 10, offersFirst: 10 },
    skip: !isAuthenticated,
  })

  // An account without a confirmed phone goes nowhere but the phone step.
  const router = useRouter()
  const user = userData?.user
  useEffect(() => {
    if (!router.isReady || !user || user.phoneConfirmedAt) return
    if (router.pathname === VERIFY_PHONE_PATH) return
    // Neither default is a return target: the dashboard is only where the
    // signed-in bounce dropped them, and carrying it would skip the wall.
    const back = [
      DEFAULT_POST_LOGIN_DESTINATION,
      POST_REGISTRATION_DESTINATION,
    ].includes(router.asPath)
      ? undefined
      : router.asPath
    void router.replace(withRedirect(VERIFY_PHONE_PATH, back))
  }, [router, user])

  // TODO: Explore adding pollInterval to keep transaction data fresh
  const {
    data: monitoredBankAccountsData,
    loading: monitoredBankAccountsQueryLoading,
    error: monitoredBankAccountsError,
    refetch: refetchMonitoredBankAccounts,
  } = useMonitoredBankAccountsQuery({
    skip: !isAuthenticated,
  })

  // TODO: Explore adding pollInterval to keep transaction data fresh
  const {
    data: monitoredBankAccountTransactionsData,
    loading: monitoredBankAccountTransactionsQueryLoading,
    refetch: refetchMonitoredBankAccountTransactions,
    error: monitoredBankAccountTransactionsError,
  } = useMonitoredBankAccountTransactionsQuery({
    skip: !isAuthenticated,
    variables: {
      sort: BankAccountTransactionSort.DateDesc,
    },
  })

  const { data: availableFeaturesData, loading: availableFeaturesLoading } =
    useAvailableFeaturesQuery({ skip: !isAuthenticated })

  // Balance is always-on user data (no demo variant), like the user record.
  const {
    data: balanceData,
    loading: balanceLoading,
    refetch: refetchBalance,
  } = useBalanceQuery({ skip: !isAuthenticated })

  // Unlinked included for card relinking; mutations refetch by this name.
  const {
    data: paymentAccountsData,
    loading: paymentAccountsLoading,
    refetch: refetchPaymentAccounts,
  } = usePaymentAccountsQuery({
    variables: { includeUnlinked: true },
    skip: !isAuthenticated,
  })

  // active: the in-effect row only; the server allows at most one.
  const {
    data: subscriptionsData,
    loading: membershipLoading,
    refetch: refetchMembership,
  } = useSubscriptionsQuery({
    variables: { active: true },
    skip: !isAuthenticated,
  })
  const membership = subscriptionsData?.subscriptions?.[0] ?? null

  const [demoOverride, setDemoOverride] = useState<boolean | null>(null)

  const availableFeatures = availableFeaturesData?.availableFeatures
  const derivedDemo =
    !availableFeaturesLoading && availableFeatures
      ? !availableFeatures.pfmBase
      : false
  const isDemo = demoOverride ?? derivedDemo

  // Stable demo dataset for the lifetime of the provider so consumers don't
  // see new transaction IDs on every render.
  const demoAccounts = useMemo(() => [demoMonitoredAccount], [])
  const demoTransactions = useMemo(() => generateDemoTransactions(), [])

  // The BE intentionally returns soft-deleted transactions: they're tombstones
  // for incrementally-syncing clients (iOS passes updatedAtStart and purges
  // local rows by the deleted flag). The web fetches full snapshots, so drop
  // them at the boundary before any consumer sees them.
  const liveTransactions = useMemo(
    () =>
      monitoredBankAccountTransactionsData?.monitoredBankAccountTransactions?.filter(
        (tx) => !tx.deleted,
      ),
    [monitoredBankAccountTransactionsData?.monitoredBankAccountTransactions],
  )

  const monitoredBankAccountsLoading = isDemo
    ? false
    : monitoredBankAccountsQueryLoading
  const monitoredBankAccountTransactionsLoading = isDemo
    ? false
    : monitoredBankAccountTransactionsQueryLoading

  const value = useMemo((): UserDataContextValue => {
    let syncState: SyncState
    if (isDemo) {
      syncState = { syncStatus: 'idle', lastSyncedAt: new Date() }
    } else if (
      monitoredBankAccountTransactionsLoading ||
      monitoredBankAccountsLoading ||
      userLoading ||
      balanceLoading
    ) {
      syncState = { syncStatus: 'syncing', lastSyncedAt: null }
    } else if (
      monitoredBankAccountTransactionsError ||
      monitoredBankAccountsError ||
      userError
    ) {
      syncState = { syncStatus: 'error', lastSyncedAt: null }
    } else {
      syncState = { syncStatus: 'idle', lastSyncedAt: new Date() }
    }

    return {
      syncState,
      user: user ?? undefined,
      monitoredBankAccounts: isDemo
        ? demoAccounts
        : (monitoredBankAccountsData?.monitoredBankAccounts ?? undefined),
      monitoredBankAccountTransactions: isDemo
        ? demoTransactions
        : liveTransactions,
      availableFeatures,
      balance: balanceData?.balance ?? undefined,
      paymentAccounts: paymentAccountsData?.paymentAccounts ?? undefined,
      membership,
      isDemo,
      demoOverride,
      setDemoOverride,
      isAuthenticated,
      userLoading,
      monitoredBankAccountsLoading,
      monitoredBankAccountTransactionsLoading,
      balanceLoading,
      paymentAccountsLoading,
      membershipLoading,
      refetchUser: isDemo ? noop : refetchUser,
      refetchMonitoredBankAccounts: isDemo
        ? noop
        : refetchMonitoredBankAccounts,
      refetchMonitoredBankAccountTransactions: isDemo
        ? noop
        : refetchMonitoredBankAccountTransactions,
      refetchBalance,
      refetchPaymentAccounts,
      refetchMembership,
    }
  }, [
    user,
    monitoredBankAccountsData?.monitoredBankAccounts,
    liveTransactions,
    monitoredBankAccountsError,
    monitoredBankAccountTransactionsError,
    userError,
    availableFeatures,
    balanceData?.balance,
    paymentAccountsData?.paymentAccounts,
    membership,
    isDemo,
    demoOverride,
    demoAccounts,
    demoTransactions,
    isAuthenticated,
    userLoading,
    monitoredBankAccountsLoading,
    monitoredBankAccountTransactionsLoading,
    balanceLoading,
    paymentAccountsLoading,
    membershipLoading,
    refetchUser,
    refetchMonitoredBankAccounts,
    refetchMonitoredBankAccountTransactions,
    refetchBalance,
    refetchPaymentAccounts,
    refetchMembership,
  ])

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  )
}

export const useUserData = () => useContext(UserDataContext)
